import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { dashboardAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/ThemeContext';
import { toast } from 'sonner';
import {
    Wifi,
    TrendingUp,
    Users,
    CreditCard,
    Clock,
    ArrowUpRight,
    Star
} from 'lucide-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
    EmptyState, 
    InfoBanner, 
    GettingStartedCard,
    HelpTooltip 
} from '../components/UsabilityComponents';
import SetupMode from '../components/SetupMode';
import { parseUtc } from '../lib/datetime';

const PLATFORM_COLORS = {
    'Google Reviews': '#FF00FF',
    'Facebook': '#1877F2',
    'Instagram': '#E4405F',
    'Trustpilot': '#00B67A'
};

/** THE SLICE CARRIES ITS SHARE, THE LEGEND CARRIES THE NAMES.
 *
 *  Platform names drawn outside the circle collided with each other and the
 *  longest one was clipped by the card's left edge. The share now sits INSIDE
 *  its own slice, where nothing can overlap and nothing can be cut off, and a
 *  slice too thin to hold a number simply shows none — the legend already
 *  names every platform. */
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (!percent || percent < 0.06) return null;
    const r = innerRadius + (outerRadius - innerRadius) * 0.62;
    const rad = -midAngle * (Math.PI / 180);
    return (
        <text x={cx + r * Math.cos(rad)} y={cy + r * Math.sin(rad)}
            fill="#FFFFFF" textAnchor="middle" dominantBaseline="central"
            style={{ fontSize: 12, fontWeight: 700, paintOrder: 'stroke',
                stroke: 'rgba(15,23,42,0.45)', strokeWidth: 2.5 }}>
            {`${Math.round(percent * 100)}%`}
        </text>
    );
};


const Dashboard = () => {
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();
    const { checkEveningPrompt } = useTheme();

    // Check for evening prompt on dashboard mount (after login)
    useEffect(() => {
        checkEveningPrompt();
    }, [checkEveningPrompt]);

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        try {
            const response = await dashboardAPI.getStats();
            setStats(response.data);
        } catch (error) {
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (timestamp) => {
        const date = parseUtc(timestamp);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const getPlatformIcon = (platform) => {
        switch (platform?.toLowerCase().replace(' ', '_')) {
            case 'google_reviews':
                return <Star className="w-4 h-4" />;
            case 'facebook':
                return <span className="text-xs font-bold">f</span>;
            case 'instagram':
                return <span className="text-xs font-bold">IG</span>;
            case 'trustpilot':
                return <Star className="w-4 h-4" />;
            default:
                return <Star className="w-4 h-4" />;
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="skeleton h-32 rounded-xl" />
                    ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="skeleton h-80 rounded-xl" />
                    <div className="skeleton h-80 rounded-xl" />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="dashboard">
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {t('dashboard.welcome')}, {user?.name?.split(' ')[0]}
                    </h1>
                    <p className="text-muted-foreground mt-1">{t('dashboard.subtitle')}</p>
                </div>
                <Badge variant="outline" className="self-start sm:self-center bg-green-50 text-green-700 border-green-200">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse" />
                    {t('dashboard.liveTracking')}
                </Badge>
            </div>

            {/* First-Time Setup Mode */}
            <SetupMode />

            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                <Card className="stat-card" data-testid="stat-total-taps" data-insight-id="dashboard.kpi.total-taps" data-insight-label="Total taps">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground mb-1 flex items-center">
                                    {t('dashboard.totalTaps')}
                                    <HelpTooltip text={t('dashboard.totalTapsHelp')} />
                                </p>
                                <p className="text-3xl font-bold text-foreground">{stats?.total_taps?.toLocaleString() || 0}</p>
                            </div>
                            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                                <Wifi className="w-5 h-5 text-blue-600" />
                            </div>
                        </div>
                        <div className="flex items-center gap-1 mt-3 text-sm">
                            <span className="text-green-600 font-medium flex items-center">
                                <ArrowUpRight className="w-4 h-4" />
                                {stats?.taps_today || 0}
                            </span>
                            <span className="text-muted-foreground">{t('dashboard.today')}</span>
                        </div>
                    </CardContent>
                </Card>

                <Card className="stat-card" data-testid="stat-active-cards" data-insight-id="dashboard.kpi.active-cards" data-insight-label="Active cards">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground mb-1 flex items-center">
                                    {t('dashboard.activeCards')}
                                    <HelpTooltip text={t('dashboard.activeCardsHelp')} />
                                </p>
                                <p className="text-3xl font-bold text-foreground">{stats?.active_cards || 0}</p>
                            </div>
                            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                                <CreditCard className="w-5 h-5 text-amber-600" />
                            </div>
                        </div>
                        <div className="flex items-center gap-1 mt-3 text-sm">
                            <span className="text-muted-foreground">{stats?.total_cards || 0} {t('dashboard.total')}</span>
                        </div>
                    </CardContent>
                </Card>

                <Card className="stat-card" data-testid="stat-employees" data-insight-id="dashboard.kpi.employees" data-insight-label="Employees">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground mb-1 flex items-center">
                                    {t('dashboard.totalEmployees')}
                                    <HelpTooltip text={t('dashboard.employeesHelp')} />
                                </p>
                                <p className="text-3xl font-bold text-foreground">{stats?.total_employees || 0}</p>
                            </div>
                            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                                <Users className="w-5 h-5 text-emerald-600" />
                            </div>
                        </div>
                        <div className="flex items-center gap-1 mt-3 text-sm">
                            <span className="text-muted-foreground">{t('dashboard.withAssignedCards')}</span>
                        </div>
                    </CardContent>
                </Card>

                <Card className="stat-card" data-testid="stat-weekly-taps" data-insight-id="dashboard.kpi.weekly-taps" data-insight-label="Weekly taps">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">{t('dashboard.tapsThisWeek')}</p>
                                <p className="text-3xl font-bold text-foreground">{stats?.taps_this_week?.toLocaleString() || 0}</p>
                            </div>
                            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                                <TrendingUp className="w-5 h-5 text-purple-600" />
                            </div>
                        </div>
                        <div className="flex items-center gap-1 mt-3 text-sm">
                            <span className="text-muted-foreground">{stats?.taps_this_month || 0} {t('dashboard.thisMonth')}</span>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Taps by Day Chart */}
                <Card className="chart-container" data-testid="chart-taps-by-day" data-insight-id="dashboard.chart.taps-by-day" data-insight-label="Taps by day">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg font-semibold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {t('dashboard.tapsThisWeek')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%" minHeight={256} debounce={1}>
                                <BarChart data={stats?.taps_by_day || []}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                    <XAxis 
                                        dataKey="date" 
                                        tick={{ fontSize: 12, fill: '#64748B' }}
                                        axisLine={{ stroke: '#E2E8F0' }}
                                    />
                                    <YAxis 
                                        tick={{ fontSize: 12, fill: '#64748B' }}
                                        axisLine={{ stroke: '#E2E8F0' }}
                                    />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: '#fff', 
                                            border: '1px solid #E2E8F0',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                        }}
                                    />
                                    <Bar 
                                        dataKey="taps" 
                                        fill="#2563EB" 
                                        radius={[4, 4, 0, 0]}
                                        maxBarSize={40}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Taps by Platform Chart */}
                <Card className="chart-container" data-testid="chart-taps-by-platform" data-insight-id="dashboard.chart.platform-mix" data-insight-label="Taps by platform">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg font-semibold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {t('dashboard.tapsByPlatform')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-80">
                            {stats?.taps_by_platform?.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%" minHeight={320} debounce={1}>
                                    <PieChart>
                                        <Pie
                                            data={stats.taps_by_platform}
                                            dataKey="taps"
                                            nameKey="platform"
                                            cx="50%"
                                            cy="46%"
                                            outerRadius={78}
                                            label={renderPieLabel}
                                            labelLine={false}
                                        >
                                            {stats.taps_by_platform.map((entry, index) => (
                                                <Cell 
                                                    key={`cell-${index}`} 
                                                    fill={PLATFORM_COLORS[entry.platform] || '#64748B'} 
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: '#fff', 
                                                border: '1px solid #E2E8F0',
                                                borderRadius: '8px'
                                            }}
                                        />
                                        {/* Every platform is named here WITH its own
                                            share, wrapped over as many lines as it
                                            needs, so a slice too thin to hold a
                                            number is still readable and nothing
                                            collides or is clipped. */}
                                        <Legend verticalAlign="bottom" height={56}
                                            iconType="circle" iconSize={9}
                                            formatter={(value, entry) => {
                                                const total = (stats.taps_by_platform || [])
                                                    .reduce((sum, d) => sum + (d.taps || 0), 0) || 1;
                                                const pct = ((entry?.payload?.taps || 0) / total) * 100;
                                                return `${value} ${pct > 0 && pct < 1 ? '<1' : Math.round(pct)}%`;
                                            }}
                                            wrapperStyle={{ fontSize: 12, lineHeight: '18px',
                                                paddingTop: 8 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex items-center justify-center text-muted-foreground">
                                    {t('dashboard.noTapData')}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Bottom Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Performers */}
                <Card data-testid="top-performers">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg font-semibold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {t('dashboard.topPerformers')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {stats?.top_performers?.length > 0 ? (
                            <div className="space-y-4">
                                {stats.top_performers.map((employee, index) => (
                                    <div 
                                        key={employee.id} 
                                        className="flex items-center gap-4 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 dark:bg-slate-800 transition-colors"
                                    >
                                        <div className={`
                                            w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                                            ${index === 0 ? 'bg-amber-100 text-amber-700' : 
                                              index === 1 ? 'bg-slate-200 dark:bg-slate-600 text-slate-700' :
                                              index === 2 ? 'bg-orange-100 text-orange-700' :
                                              'bg-slate-100 dark:bg-slate-700 text-muted-foreground'}
                                        `}>
                                            {index + 1}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-foreground truncate">{employee.name}</p>
                                            <p className="text-sm text-muted-foreground truncate">{employee.email || 'No email'}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-semibold text-foreground">{employee.tap_count}</p>
                                            <p className="text-xs text-muted-foreground">{t('cards.taps')}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="py-8 text-center text-muted-foreground">
                                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p>{t('dashboard.noEmployeeData')}</p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Recent Taps */}
                <Card data-testid="recent-taps">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <Clock className="w-5 h-5" />
                            {t('dashboard.recentTaps')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {stats?.recent_taps?.length > 0 ? (
                            <div className="space-y-3">
                                {stats.recent_taps.map((tap) => (
                                    <div 
                                        key={tap.id} 
                                        className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700"
                                    >
                                        <div className={`
                                            w-9 h-9 rounded-lg flex items-center justify-center
                                            ${tap.platform === 'google_reviews' ? 'bg-fuchsia-100 text-fuchsia-600' :
                                              tap.platform === 'facebook' ? 'bg-indigo-100 text-indigo-600' :
                                              tap.platform === 'instagram' ? 'bg-pink-100 text-pink-600' :
                                              'bg-green-100 text-green-600'}
                                        `}>
                                            {getPlatformIcon(tap.platform)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-foreground truncate">
                                                {tap.employee_name || t('cards.unassigned')}
                                            </p>
                                            <p className="text-sm text-muted-foreground">{tap.card_id}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm text-muted-foreground">{formatTime(tap.timestamp)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="py-8 text-center text-muted-foreground">
                                <Wifi className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p>{t('dashboard.noTapsRecorded')}</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default Dashboard;
